// src/pages/MetalWallet.js
import React, { useState, useContext, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import "./ItemCSS.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CartAddedPopup from "../components/CartAddedPopup";
import FAQSection, { FAQ_ITEMS } from "../components/FAQSection";
import BrikPhotoGallery from "../components/BrikPhotoGallery";
import GuideVideo from "../components/GuideVideo";
import { CartContext } from "../context/CartContext";
import {
  metalWalletProductJsonLd,
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
  makeVideoJsonLd,
} from "../seo/structuredData";
import feat5 from "../assets/new-version/5.png";
import feat6 from "../assets/new-version/6.png";
import feat7 from "../assets/new-version/7.png";
import feat8 from "../assets/new-version/8.png";
import feat9 from "../assets/new-version/9.png";

const FEATURES = [
  {
    src: feat5,
    alt: "The Metal Brik wallet holding cash, with keys attached to the integrated keyring",
  },
  {
    src: feat6,
    alt: "ID card in the quick-access front slot of the Metal Brik wallet",
  },
  {
    src: feat7,
    alt: "Credit cards in the RFID-protected middle compartment of the Metal Brik wallet",
  },
  {
    src: feat8,
    alt: "Cash held under the elastic band on the back of the Metal Brik wallet",
  },
  {
    src: feat9,
    alt: "Keys attached to the integrated key holder of the Metal Brik wallet",
  },
];

// Answers the questions shoppers actually search for. Facts sourced from
// the FAQ in src/components/FAQSection.js; keep the two in sync.
const SPECS = [
  {
    label: "Card capacity",
    value:
      "1 ID card in the quick-access front slot, plus 7-8 cards in the main compartment.",
  },
  {
    label: "Key holder",
    value:
      "Integrated, removable keyring included with every Brik. Swap it for any keyring you like.",
  },
  {
    label: "Cash",
    value:
      "Folded bills ride under the elastic band on the back. No fixed limit, capacity depends on how much else you carry.",
  },
  {
    label: "Tracking card",
    value:
      "A rechargeable tracking card is included and integrated into the wallet. See its location or ring it from your phone. Removable if you ever want the wallet alone.",
  },
  {
    label: "Battery and charging",
    value:
      "The tracking card lasts up to 6 months per charge and recharges on any standard wireless charger (charger not included).",
  },
  {
    label: "Phone compatibility",
    value:
      "Pick Apple or Android at checkout so your Brik ships with the right tracking card.",
  },
  {
    label: "RFID protection",
    value:
      "The main card compartment blocks RFID. The front ID slot stays scannable for quick access.",
  },
  {
    label: "Material",
    value:
      "Machined from solid anodized aluminum in black. Lightweight, water-resistant body built for front-pocket carry.",
  },
  {
    label: "Weight and size",
    value:
      "1.2 oz (34 g). 8.5 x 5.5 x 1.5 cm (3.3 x 2.2 x 0.6 in), about the footprint of a credit card.",
  },
  {
    label: "In the box",
    value:
      "The Brik wallet, rechargeable tracking card, keyring, two elastic bands, and setup instructions with a tracking card guide.",
  },
  {
    label: "Shipping",
    value:
      "Processed in about 1 business day, arrives in 5-7 days. Free US shipping on every order.",
  },
  {
    label: "Warranty and exchanges",
    value:
      "Structural failures during normal use are replaced under warranty. Damaged or defective orders are exchanged within 30 days.",
  },
];

// Real customer reviews of the metal Brik: 16 five-star + 4 four-star = 4.8.
// The aggregateRating in src/seo/structuredData.js mirrors this list; update
// both together when new reviews come in.
const REVIEWS = [
  { stars: 5, text: "I like having my keys, cards, cash, and tracker all in one place instead of checking three different pockets." },
  { stars: 5, text: "It took me a day to get used to, but now I don't leave the house without it." },
  { stars: 5, text: "The ID window is surprisingly convenient, especially at bars and when I'm traveling." },
  { stars: 4, text: "It's a little heavier than my old wallet, but it replaces my wallet and keychain, so it's worth it." },
  { stars: 5, text: "I bought it because I constantly lose my keys, and having everything attached has actually helped." },
  { stars: 5, text: "The Brik feels sturdy and way more useful than the minimalist wallets I've tried before." },
  { stars: 4, text: "The card section was tight at first, but after using it for a week, it broke in nicely." },
  { stars: 5, text: "My favorite part is that I can see my ID without pulling everything out." },
  { stars: 5, text: "It looks clean, holds everything I need, and has become part of my everyday routine." },
  { stars: 5, text: "I used to do the phone-wallet-keys pocket check every morning. Now it's basically just phone and Brik." },
  { stars: 4, text: "Definitely different from a normal wallet, but the key holder and tracker make it much more practical." },
  { stars: 5, text: "I got it for college because I kept misplacing my dorm keys. This would have saved me a lot of stress freshman year." },
  { stars: 5, text: "It's compact without forcing me to give up cash, keys, or the cards I actually use." },
  { stars: 5, text: "The build quality feels solid, and I've already had a few people ask me what it is." },
  { stars: 5, text: "It sounds simple, but knowing my wallet and keys are always together makes leaving the house easier." },
  { stars: 4, text: "I wish there were more color options, but the actual design is clever and useful." },
  { stars: 5, text: "The tracker slot gives me a lot more peace of mind when I'm traveling." },
  { stars: 5, text: "This is one of those products that seems unusual until you use it, and then a regular wallet feels incomplete." },
  { stars: 5, text: "I bought it as a gift for my son in college, and he uses it every day." },
  { stars: 5, text: "No more loose keys scratching my phone or falling to the bottom of my pocket." },
];

const MODELS = [
  { id: "closed", label: "Closed", src: "/models/completelyclosed.glb" },
  { id: "open", label: "Open", src: "/models/open.glb" },
  {
    id: "cash",
    label: "With cash",
    src: "/models/cashhhmoney.glb",
    orientation: "00deg 180deg 0deg",
  },
];

const METAL_WALLET = {
  id: 100,
  name: "Metal Wallet",
  description:
    "The next-generation Brik wallet, machined from solid metal.",
  price: 69.99,
  link: "/metal-wallet",
  images: ["/models/open.glb"],
};

const futura = "'Futura', sans-serif";

const MetalWallet = () => {
  const { addToCart } = useContext(CartContext);
  const [activeModel, setActiveModel] = useState(MODELS[0]);
  // Photos are the default view; the 3D models live behind a toggle.
  // A "#3d" hash (e.g. linked from the home page) opens the 3D view directly.
  const [view, setView] = useState(
    typeof window !== "undefined" && window.location.hash === "#3d"
      ? "3d"
      : "photos"
  );
  const [notice, setNotice] = useState("");
  const [featureIdx, setFeatureIdx] = useState(0);
  const [reviewPage, setReviewPage] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [platform, setPlatform] = useState(null); // "Apple" | "Android"
  const [addedVariant, setAddedVariant] = useState(METAL_WALLET);
  const reviewsRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setFeatureIdx((i) => (i + 1) % FEATURES.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const REVIEWS_PER_PAGE = 3;
  const reviewPageCount = Math.ceil(REVIEWS.length / REVIEWS_PER_PAGE);

  useEffect(() => {
    const id = setInterval(() => {
      setReviewPage((p) => (p + 1) % reviewPageCount);
    }, 5500);
    return () => clearInterval(id);
  }, [reviewPageCount]);

  const handleAddToCart = (e) => {
    if (!platform) {
      setNotice("Please select Apple or Android first.");
      setTimeout(() => setNotice(""), 2500);
      return;
    }
    const variant = {
      ...METAL_WALLET,
      id: platform === "Apple" ? "100-apple" : "100-android",
      name: `Metal Wallet (${platform})`,
      platform,
    };
    addToCart({ ...variant, quantity: 1 });
    setAddedVariant(variant);
    setNotice("Added to cart!");
    setTimeout(() => setNotice(""), 2000);
    setPopupOpen(true);
    if (typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency: "USD",
        value: 69.99,
        items: [
          {
            item_id: `metal-wallet-${platform.toLowerCase()}`,
            item_name: variant.name,
            price: 69.99,
            quantity: 1,
          },
        ],
      });
    }
    const rect = e.currentTarget.getBoundingClientRect();
    confetti({
      particleCount: 100,
      spread: 75,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
    });
  };

  const productVideoJsonLd = makeVideoJsonLd({
    name: "The Metal Brik Wallet, Explained by Its Inventor",
    description:
      "Founder Asray Gopa walks through the Metal Brik: ID up front, cards behind RFID, cash in back, keys attached, tracking card inside.",
    poster: "/videos/metal-brik-wallet-demo-poster.jpg",
    src: "/videos/metal-brik-wallet-demo.mp4",
    duration: "PT27S",
  });

  const faqJsonLd = makeFaqJsonLd(FAQ_ITEMS);

  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Shop", path: "/shop" },
    { name: "Metal Wallet", path: "/metal-wallet" },
  ]);

  return (
    <div style={{ background: "#0c0c0c", color: "white", minHeight: "100vh" }}>
      <SEO
        title="Metal Wallet with Key Holder & Tracking Card"
        description="The Metal Brik holds 7-8 cards, your ID, cash, and keys, with a rechargeable tracking card built in. $69.99, in stock, arrives in 5-7 days."
        path="/metal-wallet"
        type="product"
        keywords="metal wallet with key holder, wallet with built-in tracker, metal wallet, wallet that holds keys, minimalist metal wallet, metal wallet with ID window, keychain wallet, tracking wallet, edc wallet"
        jsonLd={[metalWalletProductJsonLd, faqJsonLd, breadcrumbJsonLd, productVideoJsonLd]}
      />
      <Header />

      <div
        style={{
          background: "#111",
          color: "#fff",
          padding: "12px 18px",
          textAlign: "center",
          fontWeight: 600,
          letterSpacing: "0.5px",
          fontFamily: futura,
        }}
      >
        In stock now · arrives in 5-7 days · free US shipping
      </div>

      {/* ===== 50/50 Split: Model (left) | Content (right) ===== */}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          alignItems: "stretch",
          minHeight: "calc(100vh - 120px)",
        }}
      >
        {/* LEFT: Photo carousel / 3D models */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            background:
              "radial-gradient(ellipse at center, #2a2a2a 0%, #0c0c0c 75%)",
            minHeight: "calc(100vh - 120px)",
          }}
        >
          {view === "photos" ? (
            <BrikPhotoGallery fill autoPlayMs={4000}>
              <button
                type="button"
                onClick={() => setView("3d")}
                style={{
                  marginTop: "4px",
                  padding: "11px 22px",
                  background: "rgba(0,0,0,0.35)",
                  color: "#ff7d1a",
                  border: "2px solid #ff7d1a",
                  borderRadius: "999px",
                  fontWeight: 700,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  fontFamily: futura,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  backdropFilter: "blur(2px)",
                }}
              >
                🕹️ Wanna play with the 3D models? →
              </button>
            </BrikPhotoGallery>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
                padding: "30px 20px",
                boxSizing: "border-box",
              }}
            >
              <model-viewer
                key={activeModel.id}
                src={activeModel.src}
                alt={`Metal Wallet: ${activeModel.label}`}
                camera-controls=""
                auto-rotate=""
                orientation={activeModel.orientation || "00deg 00deg 0deg"}
                camera-orbit="0deg 00deg 90%"
                shadow-intensity="1.2"
                exposure="1"
                ar=""
                style={{
                  width: "100%",
                  height: "58vh",
                  minHeight: "400px",
                  background: "transparent",
                  display: "block",
                }}
              />
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  marginTop: "20px",
                }}
              >
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setActiveModel(m)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "999px",
                      border:
                        m.id === activeModel.id
                          ? "2px solid #ff7d1a"
                          : "2px solid #444",
                      background:
                        m.id === activeModel.id ? "#ff7d1a" : "transparent",
                      color: m.id === activeModel.id ? "white" : "#bbb",
                      fontWeight: 700,
                      cursor: "pointer",
                      fontSize: "13px",
                      letterSpacing: "0.5px",
                      fontFamily: futura,
                      textTransform: "uppercase",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p
                style={{
                  textAlign: "center",
                  color: "#666",
                  fontSize: "12px",
                  fontFamily: futura,
                  marginTop: "10px",
                  marginBottom: 0,
                }}
              >
                Drag to rotate · scroll to zoom
              </p>
              <button
                type="button"
                onClick={() => setView("photos")}
                style={{
                  alignSelf: "center",
                  marginTop: "14px",
                  padding: "10px 20px",
                  background: "transparent",
                  color: "#bbb",
                  border: "2px solid #444",
                  borderRadius: "999px",
                  fontWeight: 700,
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  fontFamily: futura,
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                ← Back to photos
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Content */}
        <div
          style={{
            background: "#f7f7f7",
            color: "#222",
            padding: "60px 56px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "24px",
            boxSizing: "border-box",
            fontFamily: futura,
          }}
        >
          <div>
            <p
              style={{
                fontFamily: futura,
                color: "#ff7d1a",
                margin: "0 0 6px",
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "1.5px",
              }}
            >
              THE BRIK
            </p>
            <h1
              style={{
                fontFamily: futura,
                fontSize: "clamp(30px, 3.4vw, 44px)",
                margin: "0 0 16px",
                color: "#111",
                letterSpacing: "-0.5px",
                lineHeight: 1.08,
              }}
            >
              The Metal Brik: One Wallet for Your Cards, ID, Cash, Keys, and
              Tracker
            </h1>
            <p
              style={{
                fontFamily: futura,
                fontSize: "16px",
                color: "#555",
                lineHeight: 1.6,
                margin: 0,
                maxWidth: "440px",
              }}
            >
              A metal wallet with an integrated key holder and a rechargeable
              tracking card. Machined from solid anodized aluminum. Built to
              outlast the original.
            </p>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "12px",
                marginBottom: "6px",
              }}
            >
              <span
                style={{
                  color: "#999",
                  fontSize: "28px",
                  fontWeight: 600,
                  fontFamily: futura,
                  letterSpacing: "-0.5px",
                  textDecoration: "line-through",
                }}
              >
                $79.99
              </span>
              <span
                style={{
                  color: "#111",
                  fontSize: "44px",
                  fontWeight: 700,
                  fontFamily: futura,
                  letterSpacing: "-1px",
                }}
              >
                $69.99
              </span>
            </div>
            <p
              style={{
                fontFamily: futura,
                fontSize: "14px",
                color: "#666",
                margin: 0,
              }}
            >
              In stock. Processed in 1 day, arrives in 5-7 days.
            </p>
            <p
              style={{
                fontFamily: futura,
                fontSize: "13px",
                color: "#2e7d32",
                margin: "8px 0 0",
                fontWeight: 600,
              }}
            >
              ★★★★★ Rated 4.8/5 by metal Brik buyers.
            </p>
          </div>

          <div>
            <p
              style={{
                fontFamily: futura,
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#111",
                margin: "0 0 4px",
                textTransform: "uppercase",
              }}
            >
              Which phone do you use?
            </p>
            <p
              style={{
                fontFamily: futura,
                fontSize: "12px",
                color: "#777",
                margin: "0 0 10px",
              }}
            >
              So your Brik ships with the right tracking card.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {["Apple", "Android"].map((p) => {
                const selected = platform === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    style={{
                      flex: "1 1 140px",
                      padding: "14px 20px",
                      borderRadius: "8px",
                      border: selected ? "2px solid #ff7d1a" : "2px solid #ccc",
                      background: selected ? "#fff3e9" : "white",
                      color: selected ? "#ff7d1a" : "#333",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: "pointer",
                      letterSpacing: "0.5px",
                      fontFamily: futura,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {selected ? "✓ " : ""}
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={handleAddToCart}
            style={{
              alignSelf: "stretch",
              padding: "18px 36px",
              background: "#ff7d1a",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "16px",
              cursor: "pointer",
              letterSpacing: "1px",
              fontFamily: futura,
              textTransform: "uppercase",
              boxShadow: "0 4px 14px rgba(255,125,26,0.35)",
              transition: "transform 0.1s ease",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Add to cart · $69.99
          </button>
          <div
            style={{
              display: "flex",
              gap: "16px",
              fontFamily: futura,
              fontSize: "12px",
              color: "#777",
              flexWrap: "wrap",
              marginTop: "-8px",
            }}
          >
            <span>✓ Free shipping</span>
            <span>✓ In stock, ships fast</span>
          </div>
          {notice && (
            <p
              style={{
                margin: "-8px 0 0",
                color: "#2e7d32",
                fontWeight: 600,
                fontFamily: futura,
                fontSize: "14px",
              }}
            >
              {notice}
            </p>
          )}
          <button
            type="button"
            onClick={() =>
              reviewsRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            style={{
              background: "none",
              border: "none",
              color: "#666",
              fontFamily: futura,
              fontSize: "13px",
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
              marginTop: "-4px",
              textAlign: "left",
              alignSelf: "flex-start",
            }}
          >
            Read the 20 buyer reviews ↓
          </button>

          <p
            style={{
              fontSize: "12px",
              color: "#999",
              fontFamily: futura,
              margin: 0,
            }}
          >
            The plastic wallet has been discontinued (
            <a href="/wallet" style={{ color: "#666" }}>
              see old listing
            </a>
            ).
          </p>
        </div>
      </section>

      {/* ===== Features (autorotating carousel) ===== */}
      <section
        style={{
          background: "white",
          color: "#111",
          padding: "60px 24px",
        }}
      >
        <div style={{ maxWidth: "520px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: futura,
              fontSize: "clamp(26px, 3vw, 36px)",
              margin: "0 0 24px",
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            Designed to carry it all
          </h2>
          <div
            style={{
              position: "relative",
              background: "#989898",
              borderRadius: "12px",
              overflow: "hidden",
              aspectRatio: "3 / 2",
            }}
          >
            {FEATURES.map((feature, i) => (
              <img
                loading="lazy"
                key={i}
                src={feature.src}
                alt={feature.alt}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  opacity: i === featureIdx ? 1 : 0,
                  transition: "opacity 0.6s ease",
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            {FEATURES.map((_, i) => (
              <button
                key={i}
                onClick={() => setFeatureIdx(i)}
                aria-label={`Feature ${i + 1}`}
                style={{
                  width: i === featureIdx ? "28px" : "10px",
                  height: "10px",
                  borderRadius: "999px",
                  border: "none",
                  background: i === featureIdx ? "#111" : "#ccc",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== Video: founder demo ===== */}
      <GuideVideo
        heading="27 seconds with the inventor"
        caption="Asray walks through the whole carry: ID, cards, cash, keys, tracker."
        src="/videos/metal-brik-wallet-demo.mp4"
        poster="/videos/metal-brik-wallet-demo-poster.jpg"
      />

      {/* ===== Specs: the questions shoppers search for ===== */}
      <section
        style={{
          background: "#f7f7f7",
          color: "#111",
          padding: "70px 24px",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: futura,
              fontSize: "clamp(26px, 3vw, 36px)",
              margin: "0 0 8px",
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            Every spec, straight answers
          </h2>
          <p
            style={{
              fontFamily: futura,
              color: "#555",
              fontSize: "15px",
              lineHeight: 1.65,
              textAlign: "center",
              maxWidth: "680px",
              margin: "0 auto 36px",
            }}
          >
            The Metal Brik holds 1 ID in a quick-access front slot, 7-8 cards
            in an RFID-protected main compartment, and folded cash under the
            back elastic band. Keys attach to the included removable keyring,
            and a rechargeable tracking card is integrated into the wallet,
            with a battery that lasts up to 6 months per charge and refills on
            any wireless charger. The body is machined from black anodized
            aluminum, and the box includes the wallet, tracking card, keyring,
            two elastic bands, and setup instructions.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "16px",
            }}
          >
            {SPECS.map((spec) => (
              <div
                key={spec.label}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "22px 22px 20px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "14px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "#ff7d1a",
                    margin: "0 0 8px",
                  }}
                >
                  {spec.label}
                </h3>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.55,
                    color: "#333",
                    margin: 0,
                  }}
                >
                  {spec.value}
                </p>
              </div>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "28px 0 0",
            }}
          >
            Comparing options? See how the Brik stacks up against a{" "}
            <a href="/brik-vs-ridge" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Ridge-style wallet
            </a>
            , learn how the{" "}
            <a
              href="/wallet-with-built-in-tracker"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              built-in tracker
            </a>{" "}
            works, see why it's the{" "}
            <a
              href="/wallet-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              wallet college students keep buying
            </a>
            , or gift it to your whole wedding party with{" "}
            <a
              href="/groomsmen-gifts"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              groomsmen packs
            </a>
            .
          </p>
        </div>
      </section>

      {/* ===== Reviews (autorotating carousel) ===== */}
      <section
        ref={reviewsRef}
        style={{
          background: "#f7f7f7",
          color: "#111",
          padding: "80px 24px",
        }}
      >
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: futura,
              fontSize: "clamp(28px, 3.5vw, 40px)",
              margin: "0 0 8px",
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            Rated 4.8 out of 5 by metal Brik buyers.
          </h2>
          <p
            style={{
              fontFamily: futura,
              color: "#666",
              fontSize: "15px",
              textAlign: "center",
              margin: "0 0 32px",
            }}
          >
            20 reviews from people carrying the metal Brik every day.
          </p>
          <div style={{ overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                width: `${reviewPageCount * 100}%`,
                transform: `translateX(-${reviewPage * (100 / reviewPageCount)}%)`,
                transition: "transform 0.6s ease",
              }}
            >
              {Array.from({ length: reviewPageCount }).map((_, pageIdx) => {
                const start = pageIdx * REVIEWS_PER_PAGE;
                const pageItems = REVIEWS.slice(start, start + REVIEWS_PER_PAGE);
                return (
                  <div
                    key={pageIdx}
                    style={{
                      width: `${100 / reviewPageCount}%`,
                      flexShrink: 0,
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "20px",
                      padding: "4px",
                    }}
                  >
                    {pageItems.map((r) => (
                      <div
                        key={r.text}
                        style={{
                          background: "white",
                          borderRadius: "12px",
                          padding: "26px 24px 22px",
                          flex: "1 1 260px",
                          maxWidth: "320px",
                          minWidth: "250px",
                          display: "flex",
                          flexDirection: "column",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div
                          style={{
                            color: "#f5b315",
                            fontSize: "16px",
                            marginBottom: "10px",
                            letterSpacing: "2px",
                          }}
                          aria-label={`${r.stars} out of 5 stars`}
                        >
                          {"★".repeat(r.stars)}
                          <span style={{ color: "#ddd" }}>
                            {"★".repeat(5 - r.stars)}
                          </span>
                        </div>
                        <p
                          style={{
                            fontFamily: futura,
                            fontSize: "15px",
                            lineHeight: 1.55,
                            color: "#222",
                            margin: "0 0 18px",
                            fontStyle: "italic",
                            flex: 1,
                          }}
                        >
                          &ldquo;{r.text}&rdquo;
                        </p>
                        <p
                          style={{
                            fontFamily: futura,
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#888",
                            margin: 0,
                            letterSpacing: "0.3px",
                          }}
                        >
                          Verified buyer
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "24px",
            }}
          >
            {Array.from({ length: reviewPageCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => setReviewPage(i)}
                aria-label={`Review page ${i + 1}`}
                style={{
                  width: i === reviewPage ? "28px" : "10px",
                  height: "10px",
                  borderRadius: "999px",
                  border: "none",
                  background: i === reviewPage ? "#111" : "#ccc",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <FAQSection background="white" />

      <Footer />

      <CartAddedPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        productId={addedVariant.id}
        productName={addedVariant.name}
      />
    </div>
  );
};

export default MetalWallet;
